import { useRef, useState } from "react";
import { Play } from "lucide-react";

function detectVideo(url) {
  if (!url) return { type: "none" };
  const trimmed = url.trim();
  if (/^[a-zA-Z0-9_-]{11}$/.test(trimmed)) {
    return { type: "youtube", id: trimmed };
  }
  const ytMatch = trimmed.match(
    /(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/
  );
  if (ytMatch) return { type: "youtube", id: ytMatch[1] };
  return { type: "file", src: trimmed };
}

export default function VideoPlayer({ url, title = "Video", poster, vertical = false }) {
  const info = detectVideo(url);
  const [activated, setActivated] = useState(false);
  const videoRef = useRef(null);

  // Aspect ratio class
  const aspectClass = vertical ? "aspect-[9/16]" : "aspect-video";

  if (info.type === "none") {
    return (
      <div
        className={`relative w-full ${aspectClass} bg-mfp-surface border border-white/10 flex items-center justify-center`}
        data-testid="video-empty"
      >
        <div className="text-center text-mfp-muted px-6">
          <div className="font-display uppercase tracking-widest text-xs mb-2">
            Kein Video konfiguriert
          </div>
          <p className="text-xs">
            Füge im Admin eine Video-URL hinzu (MP4/MOV-Direktlink oder YouTube-URL).
          </p>
        </div>
      </div>
    );
  }

  if (info.type === "youtube") {
    const thumb = `https://img.youtube.com/vi/${info.id}/hqdefault.jpg`;
    const embed = `https://www.youtube-nocookie.com/embed/${info.id}?autoplay=1&rel=0&modestbranding=1`;
    return (
      <div className={`relative w-full ${aspectClass} bg-black overflow-hidden`} data-testid="video-youtube">
        {activated ? (
          <iframe
            src={embed}
            title={title}
            loading="lazy"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            allowFullScreen
            className="absolute inset-0 w-full h-full border-0"
          />
        ) : (
          <PosterButton
            poster={poster || thumb}
            title={title}
            onActivate={() => setActivated(true)}
          />
        )}
      </div>
    );
  }

  // Direct video file (mov/mp4/webm)
  const handleActivate = () => {
    setActivated(true);
    requestAnimationFrame(() => {
      const v = videoRef.current;
      if (v) {
        const p = v.play();
        if (p && typeof p.catch === "function") p.catch(() => {});
      }
    });
  };

  // For vertical reels use object-contain so nothing is cropped
  const objectFitClass = vertical ? "object-contain" : "object-cover";

  return (
    <div className={`relative w-full ${aspectClass} bg-black overflow-hidden`} data-testid="video-file">
      {activated ? (
        <video
          ref={videoRef}
          controls
          playsInline
          preload="auto"
          poster={poster || undefined}
          className={`absolute inset-0 w-full h-full ${objectFitClass} bg-black`}
          data-testid="video-element"
        >
          <source src={info.src} />
          Dein Browser unterstützt dieses Video-Format nicht.
        </video>
      ) : (
        <PosterButton
          poster={poster}
          title={title}
          objectFitClass={objectFitClass}
          fallback={
            <video
              src={info.src}
              muted
              playsInline
              preload="metadata"
              className={`absolute inset-0 w-full h-full ${objectFitClass}`}
              tabIndex={-1}
              aria-hidden="true"
            />
          }
          onActivate={handleActivate}
        />
      )}
    </div>
  );
}

function PosterButton({ poster, title, onActivate, fallback, objectFitClass = "object-cover" }) {
  return (
    <button
      type="button"
      onClick={onActivate}
      className="absolute inset-0 w-full h-full group"
      aria-label={`${title} abspielen`}
      data-testid="video-play-btn"
    >
      {poster ? (
        <img
          src={poster}
          alt={title}
          loading="lazy"
          className={`absolute inset-0 w-full h-full ${objectFitClass} opacity-90 group-hover:opacity-100 transition-opacity`}
        />
      ) : (
        fallback
      )}
      <span className="absolute inset-0 bg-black/30 group-hover:bg-black/20 transition-colors" />
      <span className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 flex items-center justify-center">
        <span className="h-16 w-16 md:h-20 md:w-20 bg-mfp-gold flex items-center justify-center group-hover:scale-105 transition-transform">
          <Play className="h-7 w-7 md:h-9 md:w-9 text-white fill-current" />
        </span>
      </span>
    </button>
  );
}
